import UsefulLinks from '../components/UsefulLinks';
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { HelpCircle, Bike, Waves, UtensilsCrossed, Camera, Sun, Wine } from 'lucide-react';
import SEO from '../components/SEO';

const QueFaireIleDeRe = () => {
  return (
    <>
      <SEO 
        title="Que Faire à l'Île de Ré ? Guide Complet 2026 | Activités & Visites"
        description="Découvrez que faire à l'Île de Ré : vélo, plages, phare des Baleines, villages, gastronomie, marais salants. Guide complet des meilleures activités et visites."
        keywords="que faire Île de Ré, visiter Île de Ré, activités Île de Ré, tourisme Ré, incontournables Île de Ré"
        ogImage="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200"
        url="/que-faire-ile-de-re"
      />

      {/* Hero */}
      <div className="relative h-[60vh] min-h-[400px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/images/seo/quefaire-v2.jpg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/70"></div>
        </div>
        <div className="relative h-full flex items-center justify-center text-white px-4">
          <div className="text-center max-w-4xl">
            <HelpCircle className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              Que Faire à l'Île de Ré ?
            </h1>
            <p className="text-lg sm:text-xl text-gray-200">
              Guide complet des activités, visites et expériences incontournables
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Introduction */}
        <section className="mb-16">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Vous vous demandez <strong>que faire à l'Île de Ré</strong> pendant vos vacances ? 
            Cette île de Charente-Maritime offre une diversité exceptionnelle d'activités pour tous les goûts : 
            sportives, culturelles, gastronomiques ou nature. Voici notre guide complet pour ne rien manquer.
          </p>
        </section>

        {/* Top 10 Incontournables */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">🏆 Top 10 des Incontournables</h2>
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-6 rounded-lg shadow-md border-l-4 border-[#c17a3a]">
              <div className="flex items-start gap-4">
                <div className="text-3xl font-bold text-[#c17a3a] min-w-[40px]">1</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">🚴 Faire du Vélo sur les Pistes Cyclables</h3>
                  <p className="text-gray-700">
                    <strong>L'activité #1 de l'île !</strong> Plus de 100 km de pistes plates et sécurisées traversent villages, 
                    forêts de pins, marais salants et vignobles. Location partout (10-15€/jour). À faire absolument !
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
              <div className="flex items-start gap-4">
                <div className="text-3xl font-bold text-blue-500 min-w-[40px]">2</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">🏖️ Profiter des Plages</h3>
                  <p className="text-gray-700">
                    100 km de côtes avec plages pour tous : familiales (Redoute), paradisiaques (Trousse-Chemise), 
                    sauvages (Gollandières), surf (Petit Bec). Eau translucide et sable fin garantis.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
              <div className="flex items-start gap-4">
                <div className="text-3xl font-bold text-green-500 min-w-[40px]">3</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">🗼 Monter au Phare des Baleines</h3>
                  <p className="text-gray-700">
                    257 marches pour une vue panoramique à 360° sur l'île et l'océan. Musée et vieille tour inclus. 
                    Coucher de soleil inoubliable. Tarif : 3,50€. <strong>Incontournable !</strong>
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500">
              <div className="flex items-start gap-4">
                <div className="text-3xl font-bold text-purple-500 min-w-[40px]">4</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">🏘️ Visiter Saint-Martin-de-Ré</h3>
                  <p className="text-gray-700">
                    Capitale classée UNESCO. Fortifications Vauban, port animé, ruelles commerçantes, restaurants gastronomiques. 
                    Marché quotidien (matin). La plus belle ville de l'île.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-yellow-500">
              <div className="flex items-start gap-4">
                <div className="text-3xl font-bold text-yellow-500 min-w-[40px]">5</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">🧂 Découvrir les Marais Salants</h3>
                  <p className="text-gray-700">
                    Visite guidée avec saunier (producteur de sel). Découverte du métier traditionnel, 
                    récolte du sel, dégustation. Expérience authentique et pédagogique (8-12€, 1h30).
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-500">
              <div className="flex items-start gap-4">
                <div className="text-3xl font-bold text-red-500 min-w-[40px]">6</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">🦪 Déguster Huîtres au Port</h3>
                  <p className="text-gray-700">
                    Cabanes ostréicoles sur tous les ports (Ars, Les Portes, La Flotte). Dégustation sur place 
                    avec vue sur les bateaux. Huîtres fraîches à partir de 6€ la douzaine. Un régal !
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-indigo-500">
              <div className="flex items-start gap-4">
                <div className="text-3xl font-bold text-indigo-500 min-w-[40px]">7</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">🎨 Explorer les Villages</h3>
                  <p className="text-gray-700">
                    <strong>Ars-en-Ré</strong> (clocher noir/blanc, galeries d'art), <strong>La Flotte</strong> (Plus Beau Village de France), 
                    <strong>Les Portes-en-Ré</strong> (authentique), <strong>Loix</strong> (marais salants). Chacun a son charme unique.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-pink-500">
              <div className="flex items-start gap-4">
                <div className="text-3xl font-bold text-pink-500 min-w-[40px]">8</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">🏄 Sports Nautiques</h3>
                  <p className="text-gray-700">
                    Voile, paddle, kayak, kitesurf, planche à voile, surf. Écoles et locations dans tous les villages côtiers. 
                    Conditions idéales pour débutants et experts. À partir de 25€/session.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-teal-500">
              <div className="flex items-start gap-4">
                <div className="text-3xl font-bold text-teal-500 min-w-[40px]">9</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">🍷 Visiter les Vignobles</h3>
                  <p className="text-gray-700">
                    Plusieurs domaines viticoles proposent visites et dégustations (Pineau, Cognac, vins de Charente). 
                    Découverte du terroir local. Gratuit à 15€. Produits en vente directe.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-orange-500">
              <div className="flex items-start gap-4">
                <div className="text-3xl font-bold text-orange-500 min-w-[40px]">10</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">🌅 Assister aux Couchers de Soleil</h3>
                  <p className="text-gray-700">
                    Les couchers de soleil sur l'Atlantique sont spectaculaires. Meilleurs spots : <strong>Phare des Baleines</strong>, 
                    <strong>Plage de la Conche</strong>, <strong>Port d'Ars</strong>. À ne manquer sous aucun prétexte !
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Programme type */}
        <section className="mb-16 bg-blue-50 p-8 rounded-lg">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">📅 Programme Type d'une Journée Parfaite</h2>
          <div className="space-y-4 text-gray-700">
            <div className="flex gap-4">
              <div className="font-semibold min-w-[100px]">☀️ Matin</div>
              <div>Vélo jusqu'à la plage (30 min) → Baignade et farniente → Déjeuner fruits de mer au port</div>
            </div>
            <div className="flex gap-4">
              <div className="font-semibold min-w-[100px]">🌤️ Après-midi</div>
              <div>Visite d'un village pittoresque (Saint-Martin ou Ars) → Shopping artisanal → Glace artisanale</div>
            </div>
            <div className="flex gap-4">
              <div className="font-semibold min-w-[100px]">🌆 Soir</div>
              <div>Retour villa pour spa de nage → BBQ dans le jardin → Observation des étoiles (ciel préservé)</div>
            </div>
          </div>
        </section>

        {/* Selon durée séjour */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">⏱️ Que Faire Selon la Durée du Séjour ?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-lg border-t-4 border-yellow-500">
              <h3 className="text-xl font-bold mb-4">Week-end (2-3 jours)</h3>
              <ul className="space-y-2 text-gray-700">
                <li>✓ Vélo vers 1-2 plages</li>
                <li>✓ Saint-Martin-de-Ré</li>
                <li>✓ Phare des Baleines</li>
                <li>✓ Dégustation huîtres</li>
                <li>✓ 1 marché local</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border-t-4 border-orange-500">
              <h3 className="text-xl font-bold mb-4">Semaine (7 jours)</h3>
              <ul className="space-y-2 text-gray-700">
                <li>✓ Tout du week-end +</li>
                <li>✓ Tour complet de l'île à vélo</li>
                <li>✓ Tous les villages</li>
                <li>✓ Visite marais salants</li>
                <li>✓ Sports nautiques</li>
                <li>✓ Vignobles</li>
                <li>✓ Marchés nocturnes</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border-t-4 border-red-500">
              <h3 className="text-xl font-bold mb-4">2 semaines+</h3>
              <ul className="space-y-2 text-gray-700">
                <li>✓ Rythme slow, profiter pleinement</li>
                <li>✓ Excursion La Rochelle</li>
                <li>✓ Île d'Oléron (voisine)</li>
                <li>✓ Cours de voile/kite</li>
                <li>✓ Golf (9 trous)</li>
                <li>✓ Pêche à pied régulière</li>
              </ul>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-[#c17a3a] to-[#a66830] text-white p-12 rounded-lg text-center">
          <h2 className="text-3xl font-bold mb-4">Prêt à Découvrir l'Île de Ré ?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Réservez la Villa Nomade et profitez d'un point de chute idéal pour explorer toutes ces merveilles.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#c17a3a] hover:bg-gray-100">
                Réserver maintenant
              </Button>
            </Link>
            <Link to="/activites-ile-de-re">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Toutes les activités
              </Button>
            </Link>
          </div>
        </section>
      </div>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Que faire à l'Île de Ré ?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Les incontournables sont : vélo sur 100km de pistes, plages exceptionnelles, phare des Baleines, villages pittoresques, dégustation huîtres, marais salants, sports nautiques."
              }
            }
          ]
        })}
      </script>
    </>
  );
};

      <UsefulLinks />
export default QueFaireIleDeRe;